import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Pencil, Plus, Search, Trash2, X } from "lucide-react";

import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { Modal } from "../components/common/Modal";
import {
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  type UserCreate,
  type UserUpdate,
} from "../api/users";
import { useDevices } from "../hooks/useDevices";
import { useDevicesStore } from "../store/devicesStore";
import { useAuthStore } from "../store/authStore";
import { formatApiError } from "../lib/apiError";
import type { Permission, User } from "../types";

interface FormState {
  id?: number;
  username: string;
  email: string;
  password: string;
  role: "admin" | "user";
  is_active: boolean;
  can_manage_users: boolean;
  can_manage_devices: boolean;
  can_access_config: boolean;
  can_view_live: boolean;
  can_view_recordings: boolean;
  can_download_recordings: boolean;
  assigned_device_ids: number[];
}

const EMPTY_FORM: FormState = {
  username: "",
  email: "",
  password: "",
  role: "user",
  is_active: true,
  can_manage_users: false,
  can_manage_devices: false,
  can_access_config: false,
  can_view_live: true,
  can_view_recordings: false,
  can_download_recordings: false,
  assigned_device_ids: [],
};

const PERMISSION_LABELS: { key: Permission; label: string; help: string }[] = [
  { key: "can_manage_users", label: "Gestionar usuarios", help: "Puede crear, editar y eliminar usuarios." },
  { key: "can_manage_devices", label: "Administrar dispositivos", help: "Puede agregar, editar, reiniciar y eliminar dispositivos." },
  { key: "can_access_config", label: "Configurar dispositivos", help: "Puede ver y modificar la configuracion de los DVRs (red, encode, NTP)." },
  { key: "can_view_live", label: "Ver vista en vivo", help: "Puede ver streams y snapshots en tiempo real." },
  { key: "can_view_recordings", label: "Ver grabaciones", help: "Puede buscar y reproducir grabaciones." },
  { key: "can_download_recordings", label: "Descargar grabaciones", help: "Puede encolar descargas de grabaciones." },
];

export default function UsersPage() {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: listUsers });
  useDevices(); // popula store
  const devices = useDevicesStore((s) => s.devices);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deviceQuery, setDeviceQuery] = useState("");

  const isEditingSelf = mode === "edit" && form.id === me?.id;

  const openCreate = () => {
    setMode("create");
    setForm(EMPTY_FORM);
    setDeviceQuery("");
    setOpen(true);
  };

  const openEdit = (u: User) => {
    setMode("edit");
    setDeviceQuery("");
    setForm({
      id: u.id,
      username: u.username,
      email: u.email ?? "",
      password: "",
      role: u.role,
      is_active: u.is_active,
      can_manage_users: u.can_manage_users,
      can_manage_devices: u.can_manage_devices,
      can_access_config: u.can_access_config,
      can_view_live: u.can_view_live,
      can_view_recordings: u.can_view_recordings,
      can_download_recordings: u.can_download_recordings,
      assigned_device_ids: u.accessible_device_ids ?? [],
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const basePayload = {
        email: form.email || undefined,
        role: form.role,
        is_active: form.is_active,
        can_manage_users: form.can_manage_users,
        can_manage_devices: form.can_manage_devices,
        can_access_config: form.can_access_config,
        can_view_live: form.can_view_live,
        can_view_recordings: form.can_view_recordings,
        can_download_recordings: form.can_download_recordings,
        assigned_device_ids: form.role === "admin" ? [] : form.assigned_device_ids,
      };

      if (mode === "create") {
        if (!form.username.trim()) {
          toast.error("Usuario requerido");
          return;
        }
        if (!form.password) {
          toast.error("Contrasena requerida");
          return;
        }
        const payload: UserCreate = {
          username: form.username,
          password: form.password,
          ...basePayload,
        };
        await createUser(payload);
        toast.success("Usuario creado");
      } else {
        if (!form.id) return;
        const patch: UserUpdate = { ...basePayload };
        if (form.password) {
          patch.password = form.password;
        }
        await updateUser(form.id, patch);
        toast.success("Usuario actualizado");
      }
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast.error(formatApiError(e, "Error al guardar"));
    }
  };

  const toggleActive = async (id: number, is_active: boolean) => {
    try {
      await updateUser(id, { is_active });
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDelete = async (u: User) => {
    if (u.id === me?.id) {
      toast.error("No podes eliminarte a vos mismo");
      return;
    }
    if (!confirm(`Eliminar usuario "${u.username}"?`)) return;
    try {
      await deleteUser(u.id);
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleDevice = (deviceId: number) => {
    setForm((f) => {
      const has = f.assigned_device_ids.includes(deviceId);
      return {
        ...f,
        assigned_device_ids: has
          ? f.assigned_device_ids.filter((id) => id !== deviceId)
          : [...f.assigned_device_ids, deviceId],
      };
    });
  };

  const permissionBadges = (u: User) => {
    if (u.role === "admin") return <span className="text-accent text-xs">admin (todo)</span>;
    const flags = PERMISSION_LABELS.filter((p) => u[p.key]);
    if (!flags.length) return <span className="text-muted text-xs">sin permisos</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {flags.map((f) => (
          <span key={f.key} className="text-[10px] bg-line text-text px-1.5 py-0.5 rounded" title={f.help}>
            {f.label}
          </span>
        ))}
      </div>
    );
  };

  const deviceCountLabel = (u: User) => {
    if (u.role === "admin") return "todos";
    return `${u.accessible_device_ids?.length ?? 0} / ${devices.length}`;
  };

  const sortedDevices = useMemo(
    () =>
      [...devices].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
      ),
    [devices],
  );

  const filteredDevices = useMemo(() => {
    const q = deviceQuery.trim().toLowerCase();
    if (!q) return sortedDevices;
    return sortedDevices.filter(
      (d) => d.name.toLowerCase().includes(q) || d.host.toLowerCase().includes(q),
    );
  }, [sortedDevices, deviceQuery]);

  const visibleAllSelected =
    filteredDevices.length > 0 &&
    filteredDevices.every((d) => form.assigned_device_ids.includes(d.id));

  const toggleAllVisible = () => {
    setForm((f) => {
      const visibleIds = filteredDevices.map((d) => d.id);
      if (visibleAllSelected) {
        const visibleSet = new Set(visibleIds);
        return {
          ...f,
          assigned_device_ids: f.assigned_device_ids.filter((id) => !visibleSet.has(id)),
        };
      }
      const merged = new Set([...f.assigned_device_ids, ...visibleIds]);
      return { ...f, assigned_device_ids: Array.from(merged) };
    });
  };

  return (
    <div className="p-4 overflow-auto h-full">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold">Usuarios</h1>
        <Button variant="primary" size="sm" onClick={openCreate}>
          <Plus size={14} /> Nuevo
        </Button>
      </div>
      <div className="border border-line rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-panel border-b border-line">
            <tr className="text-xs text-muted">
              <th className="text-left px-3 py-2">Usuario</th>
              <th className="text-left px-3 py-2">Rol</th>
              <th className="text-left px-3 py-2">Permisos</th>
              <th className="text-left px-3 py-2">Dispositivos</th>
              <th className="text-left px-3 py-2">Activo</th>
              <th className="text-left px-3 py-2">Ultimo login</th>
              <th className="text-right px-3 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users?.map((u) => (
              <tr key={u.id} className="border-b border-line hover:bg-panel align-top">
                <td className="px-3 py-1.5">{u.username}</td>
                <td className="px-3 py-1.5">{u.role}</td>
                <td className="px-3 py-1.5">{permissionBadges(u)}</td>
                <td className="px-3 py-1.5 text-muted">{deviceCountLabel(u)}</td>
                <td className="px-3 py-1.5">
                  <input
                    type="checkbox"
                    checked={u.is_active}
                    onChange={(e) => toggleActive(u.id, e.target.checked)}
                  />
                </td>
                <td className="px-3 py-1.5 text-muted">
                  {u.last_login ? new Date(u.last_login).toLocaleString() : "-"}
                </td>
                <td className="px-3 py-1.5 text-right space-x-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)} title="Editar">
                    <Pencil size={12} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(u)}
                    title="Eliminar"
                  >
                    <Trash2 size={12} />
                  </Button>
                </td>
              </tr>
            ))}
            {!users?.length && (
              <tr>
                <td colSpan={7} className="text-center text-muted py-6">
                  Sin usuarios.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={mode === "create" ? "Nuevo usuario" : `Editar ${form.username}`}
        width="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Usuario"
              value={form.username}
              disabled={mode === "edit"}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <Input
              label={mode === "edit" ? "Contrasena (vacio = no cambiar)" : "Contrasena"}
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-muted">Rol</span>
              <select
                className="bg-bg border border-line rounded px-2 py-1.5 text-sm"
                value={form.role}
                onChange={(e) =>
                  setForm({ ...form, role: e.target.value as "admin" | "user" })
                }
                disabled={isEditingSelf}
              >
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                disabled={isEditingSelf}
              />
              Activo
            </label>
          </div>

          {form.role === "admin" ? (
            <div className="text-xs text-accent border border-line rounded p-3 bg-panel">
              El rol <b>admin</b> tiene todos los permisos y acceso a todos los dispositivos. Los flags individuales y la lista de dispositivos no aplican.
            </div>
          ) : (
            <>
              <div>
                <div className="text-xs text-muted mb-2">Permisos</div>
                <div className="grid grid-cols-2 gap-2">
                  {PERMISSION_LABELS.map(({ key, label, help }) => (
                    <label key={key} className="flex items-start gap-2 text-xs" title={help}>
                      <input
                        type="checkbox"
                        checked={form[key]}
                        onChange={(e) =>
                          setForm({ ...form, [key]: e.target.checked } as FormState)
                        }
                        className="mt-0.5"
                      />
                      <span>
                        <span className="text-text">{label}</span>
                        <span className="block text-muted text-[10px]">{help}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="text-xs text-muted">
                    Dispositivos accesibles ({form.assigned_device_ids.length}/{sortedDevices.length})
                    {deviceQuery && (
                      <span className="ml-1">- visibles {filteredDevices.length}</span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleAllVisible}
                    disabled={filteredDevices.length === 0}
                    title={
                      deviceQuery
                        ? "Marca o desmarca todos los dispositivos visibles segun el filtro"
                        : "Marca o desmarca todos los dispositivos"
                    }
                  >
                    {visibleAllSelected ? "Desmarcar" : "Marcar"} {deviceQuery ? "visibles" : "todos"}
                  </Button>
                </div>
                <div className="relative mb-2">
                  <Search
                    size={12}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
                  />
                  <input
                    type="text"
                    value={deviceQuery}
                    onChange={(e) => setDeviceQuery(e.target.value)}
                    placeholder="Buscar abonado o IP..."
                    className="bg-bg border border-line rounded pl-6 pr-6 py-1 text-xs text-text placeholder-muted focus:outline-none focus:border-accent w-full"
                  />
                  {deviceQuery && (
                    <button
                      type="button"
                      onClick={() => setDeviceQuery("")}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted hover:text-text"
                      title="Limpiar"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div className="border border-line rounded max-h-48 overflow-y-auto">
                  {sortedDevices.length === 0 && (
                    <div className="text-muted text-xs px-3 py-2">Sin dispositivos.</div>
                  )}
                  {sortedDevices.length > 0 && filteredDevices.length === 0 && (
                    <div className="text-muted text-xs px-3 py-2">
                      Sin resultados para "{deviceQuery}".
                    </div>
                  )}
                  {filteredDevices.map((d) => (
                    <label
                      key={d.id}
                      className="flex items-center gap-2 px-3 py-1.5 text-xs border-b border-line last:border-b-0 hover:bg-panel"
                    >
                      <input
                        type="checkbox"
                        checked={form.assigned_device_ids.includes(d.id)}
                        onChange={() => toggleDevice(d.id)}
                      />
                      <span className="flex-1">{d.name}</span>
                      <span className="text-muted">{d.host}:{d.http_port}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="primary" onClick={submit}>
            {mode === "create" ? "Crear" : "Guardar"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
